import { APP_BASE_HREF, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { checkUrlAbsolute, concatUrl } from '@lvflanders/bar';
import { resolveSignalValue } from '@lvflanders/bar-ng';

import { _BarInteractiveElementBehavior } from '../interactive-element/bar-interactive-element-behavior';
import { BaasLinkOptions } from './baas-link-options.type';

@Component({
    selector: 'baas-link',
    templateUrl: './baas-link.html',
    imports: [MatIcon, NgTemplateOutlet, RouterLink, RouterLinkActive, MatTooltipModule]
})
export class BaasLink {
    readonly #clickableElementBehavior = inject(_BarInteractiveElementBehavior);
    readonly #baseHref = inject<string>(APP_BASE_HREF, { optional: true });

    readonly item = input.required<BaasLinkOptions>();
    readonly linkClass = input<string>();
    readonly ommitCssClass = input<boolean>();
    // NOTE needed for ag grid cells with links to avoid losing the cell borders
    /** Whether to inject a zero whiteline if hidden. */
    readonly zeroWhiteLineIfHidden = input(false);

    readonly clicked = output();
    /**
    * performs the union of:
        - internal needed classes (disabled)
        - css classes needed for component layout
        - custom css classed defined by consumer
    */
    protected readonly linkClasses = computed(() => {
        const classes: string[] = [];

        if (this.disabled()) {
            classes.push('disabled');
        }

        if (this.linkClass()) {
            classes.push(this.linkClass());
        }

        if (this.ommitCssClass() !== true) {
            const cssClass = this.item().cssClass;
            if (cssClass) {
                classes.push(cssClass);
            }
        }

        return classes.join(' ').trim();
    });

    protected readonly iconExists = computed(() => {
        return !!this.item().icon;
    });

    protected readonly iconHasPosition = computed(() => {
        return this.iconExists() && !!this.item().icon.position;
    });

    protected readonly iconHasLeftPosition = computed(() => {
        return this.iconHasPosition() && this.item().icon.position === 'left' ? true : false;
    });

    protected readonly iconHasRightPosition = computed(() => {
        return this.iconHasPosition() && this.item().icon.position === 'right' ? true : false;
    });

    protected readonly iconHasTopPosition = computed(() => {
        return this.iconHasPosition() && this.item().icon.position === 'top' ? true : false;
    });

    protected readonly disabled = computed(() => resolveSignalValue(this.item().disabled));

    protected readonly hidden = computed(() => resolveSignalValue(this.item().hidden));

    /** Computes the href value, prepending base href to routerUrl when needed */
    protected readonly href = computed(() => {
        const item = this.item();
        if (item.href) {
            return item.href;
        }
        if (item.routerUrl) {
            if (checkUrlAbsolute(item.routerUrl)) {
                return item.routerUrl;
            }
            return concatUrl(this.#baseHref || '/', item.routerUrl);
        }
        return null;
    });

    protected async linkClick(event): Promise<void> {
        this.clicked.emit();
        await this.#clickableElementBehavior.linkClick(event, this.item());
    }

    // invoked in case an action is defined and a routerLink
    protected async linkClickWithRouterLink(event): Promise<void> {
        await this.#clickableElementBehavior.linkClickWithRouterCommands(event, this.item());
    }
}
